import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  // Navigation
  dashboard: { en: "Executive Cockpit for Mining", pt: "Cockpit Executivo para a Mineração" },
  operations: { en: "Operations", pt: "Operações" },
  finance: { en: "Finance", pt: "Finanças" },
  hr: { en: "Human Resources", pt: "Recursos Humanos" },
  esg: { en: "ESG", pt: "ESG" },
  market: { en: "Market Intelligence", pt: "Informações de Mercado" },
  
  // Headers
  integratedView: { en: "Integrated view of performance, risk and execution", pt: "Visão integrada de performance, risco e execução" },
  kpiHighlights: { en: "KPI Highlights", pt: "Destaques KPI" },
  areaOverview: { en: "Area Overview", pt: "Visão por Área" },
  executiveAlerts: { en: "Executive Alerts", pt: "Alertas Executivos" },
  decisionsRequired: { en: "Decisions Required", pt: "Decisões Necessárias" },
  
  // KPI Labels
  productionVsPlan: { en: "Production vs Plan", pt: "Produção vs Plano" },
  oreProcessed: { en: "Ore Processed", pt: "Minério Processado" },
  recoveryRate: { en: "Recovery Rate", pt: "Taxa de Recuperação" },
  revenue: { en: "Revenue", pt: "Receita" },
  ebitda: { en: "EBITDA", pt: "EBITDA" },
  cashAvailable: { en: "Cash Available", pt: "Caixa Disponível" },
  capexExecution: { en: "CAPEX Execution", pt: "Execução CAPEX" },
  headcount: { en: "Headcount", pt: "Efectivos" },
  turnoverRate: { en: "Turnover Rate", pt: "Taxa Rotatividade" },
  criticalRolesFilled: { en: "Critical Roles Filled", pt: "Funções Críticas Preenchidas" },
  ltifr: { en: "LTIFR / Safety", pt: "LTIFR / Segurança" },
  environmentalCompliance: { en: "Environmental Compliance", pt: "Conformidade Ambiental" },
  
  // Status
  strong: { en: "Strong", pt: "Forte" },
  inline: { en: "On Track", pt: "Em Linha" },
  attention: { en: "Attention", pt: "Atenção" },
  pressure: { en: "Under Pressure", pt: "Sob Pressão" },
  
  // Comparisons
  vsBudget: { en: "vs Budget", pt: "vs Orçamento" },
  vsPrevious: { en: "vs Previous", pt: "vs Anterior" },
  
  // Finance Section
  financialPerformance: { en: "Financial Performance", pt: "Performance Financeira" },
  liquidityCashFlow: { en: "Liquidity & Cash Flow", pt: "Liquidez e Cash Flow" },
  debtFinancing: { en: "Debt & Financing", pt: "Dívida e Financiamento" },
  capexInvestment: { en: "CAPEX & Investment", pt: "CAPEX e Investimento" },
  revenueCommercial: { en: "Revenue & Commercial", pt: "Receita e Comercial" },
  costsEfficiency: { en: "Costs & Efficiency", pt: "Custos e Eficiência" },
  workingCapital: { en: "Working Capital", pt: "Capital de Giro" },
  riskCompliance: { en: "Risk & Compliance", pt: "Risco e Compliance" },
  strategyOutlook: { en: "Strategy & Outlook", pt: "Estratégia e Outlook" },
  realVsBudget: { en: "Real vs Budget", pt: "Real vs Orçamento" },
  
  // Charts
  monthlyTrend: { en: "Monthly Trend", pt: "Tendência Mensal" },
  yearComparison: { en: "Year Comparison", pt: "Comparação Anual" },
  executiveSummary: { en: "Key Messages", pt: "Mensagens-Chave" },
  
  // Chatbot
  askQuestion: { en: "Ask a question...", pt: "Faça uma pergunta..." },
  suggestedQuestions: { en: "Suggested Questions", pt: "Perguntas Sugeridas" },
  sendMessage: { en: "Send", pt: "Enviar" },
  needMoreDetail: { en: "Would you like more detail?", pt: "Gostaria de mais detalhes?" },
  yesDeepen: { en: "Yes, go deeper", pt: "Sim, aprofundar" },
  noSufficient: { en: "No, this is sufficient", pt: "Não, é suficiente" },
  
  // Placeholders
  moduleInDevelopment: { en: "Module in Development", pt: "Módulo em Desenvolvimento" },
  placeholderText: { 
    en: "This area is part of the corporate dashboard architecture and will be activated in a subsequent phase, after completion of the indicator model, validation of supporting information and final design of the navigation experience.", 
    pt: "Esta área faz parte da arquitectura do dashboard corporativo e será activada numa fase subsequente, após conclusão do modelo de indicadores, validação da informação de suporte e desenho final da experiência de navegação." 
  },
  activationNote: { en: "Activation scheduled for subsequent phase of the dashboard program.", pt: "Activação prevista em fase subsequente do programa de dashboard." },
  
  // Operations
  operationsSubtitle: { en: "Operational execution, productivity and mining chain performance", pt: "Execução operacional, produtividade e performance da cadeia mineira" },
  
  // HR
  hrSubtitle: { en: "Human structure, critical talent, retention and organizational capacity", pt: "Estrutura humana, talento crítico, retenção e capacidade organizacional" },
  
  // ESG
  esgSubtitle: { en: "Safety, environment, communities, compliance and license to operate", pt: "Segurança, ambiente, comunidades, compliance e licença para operar" },
  
  // Market
  diamondPriceIndex: { en: "Diamond Price Index", pt: "Índice de Preços de Diamantes" },
  angolaEconomy: { en: "Angola Economy", pt: "Economia de Angola" },
  industryOverview: { en: "Industry Overview", pt: "Visão da Indústria" },
  gdpGrowth: { en: "GDP Growth", pt: "Crescimento PIB" },
  inflation: { en: "Inflation", pt: "Inflação" },
  exchangeRate: { en: "Exchange Rate", pt: "Taxa de Câmbio" },
  globalProduction: { en: "Global Production", pt: "Produção Global" },
  angolaShare: { en: "Angola Market Share", pt: "Quota Angola" },
  labGrownShare: { en: "Lab-Grown Share", pt: "Quota Sintéticos" },
  
  // Actions
  viewDetails: { en: "View Details", pt: "Ver Detalhes" },
  download: { en: "Download", pt: "Descarregar" },
  selectYear: { en: "Select Year", pt: "Seleccionar Ano" },
  selectPeriod: { en: "Select Period", pt: "Seleccionar Período" },
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('pt');

  const t = (key) => {
    if (typeof key === 'object' && key !== null) {
      return key[language] || key.en || '';
    }
    const translation = translations[key];
    if (!translation) return key;
    return translation[language] || translation.en || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'pt' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
